/**
 * mountain-stamps — baked EROSION STAMPS for massifs (Kenny's idea).
 *
 * Bakes a 2x2 atlas of eroded-mountain reliefs ONCE into a render target:
 * a radial plug base (like our massifs) run through runevision's "Clean
 * Terrain Erosion Filter" (https://www.shadertoy.com/view/33cXW8, MIT,
 * runevision <- Fewes <- clayjohn <- guil/iq). The filter grows branching
 * drainage gullies down the slopes — real eroded-mountain relief.
 *
 * The stamps are applied per MASSIF in the planet shader as tangent-space
 * normal perturbation + crevice darkening: detail lives in one small texture,
 * not in vertices — the whole point for XR.
 *
 * Tuning (viz page reads URL params into globalThis.STAMP_PARAMS):
 *   scale (erosion feature size), strength (gully depth),
 *   power (peak sharpness), cell (gully length), octaves
 */

import {
	LinearFilter,
	LinearMipmapLinearFilter,
	Mesh,
	OrthographicCamera,
	PlaneGeometry,
	Scene,
	ShaderMaterial,
	WebGLRenderTarget,
} from 'three';

export const STAMP_DEFAULTS = { scale: 0.28, strength: 0.2, power: 0.6, cell: 1.0, octaves: 5 };

// ---- erosion filter, ported from the shadertoy (Common + Buffer A) ----------
export const EROSION_GLSL = /* glsl */`
	#define PI 3.14159265358979

	// PCG-style bit hash. The shadertoy's original hash (fract of
	// x.x*x.y*(x.x+x.y)) collapses in float32 once coords exceed ~1e3: the
	// product overflows the mantissa, fract() quantizes, and gradients become
	// lattice-correlated -> parallel diagonal ridge artifacts at high octaves.
	vec2 hash(in vec2 x) {
		uvec2 v = floatBitsToUint(x);
		v = v * 1664525u + 1013904223u;
		v.x += v.y * 1664525u; v.y += v.x * 1664525u;
		v ^= v >> 16u;
		v.x += v.y * 1664525u; v.y += v.x * 1664525u;
		v ^= v >> 16u;
		return vec2(v & uvec2(0x00FFFFFFu)) * (2.0 / 16777215.0) - 1.0;
	}

	// iq's gradient noise with analytic derivatives (XdXBRH)
	vec3 noised(in vec2 p) {
		vec2 i = floor(p);
		vec2 f = fract(p);
		vec2 u = f*f*f*(f*(f*6.0-15.0)+10.0);
		vec2 du = 30.0*f*f*(f*(f-2.0)+1.0);
		vec2 ga = hash(i + vec2(0.0, 0.0));
		vec2 gb = hash(i + vec2(1.0, 0.0));
		vec2 gc = hash(i + vec2(0.0, 1.0));
		vec2 gd = hash(i + vec2(1.0, 1.0));
		float va = dot(ga, f - vec2(0.0, 0.0));
		float vb = dot(gb, f - vec2(1.0, 0.0));
		float vc = dot(gc, f - vec2(0.0, 1.0));
		float vd = dot(gd, f - vec2(1.0, 1.0));
		return vec3(va + u.x*(vb-va) + u.y*(vc-va) + u.x*u.y*(va-vb-vc+vd),
			ga + u.x*(gb-ga) + u.y*(gc-ga) + u.x*u.y*(ga-gb-gc+gd) +
			du * (u.yx*(va-vb-vc+vd) + vec2(vb, vc) - va));
	}

	// Gullies: directional stripe noise with analytic derivatives, stripes run
	// ALONG the slope; steeper slopes -> higher frequency (runevision)
	vec3 Gullies(in vec2 p, vec2 slope) {
		vec2 sideDir = slope.yx * vec2(-1.0, 1.0) * 2.0 * PI;
		vec2 pInt = floor(p);
		vec2 pFrac = fract(p);
		vec3 heightAndSlope = vec3(0.0);
		float weightSum = 0.0;
		for (int i = -1; i <= 2; i++) {
			for (int j = -1; j <= 2; j++) {
				vec2 gridOffset = vec2(i, j);
				vec2 gridPoint = pInt + gridOffset;
				vec2 randomOffset = hash(gridPoint) * 0.5;
				vec2 v = pFrac - gridOffset - randomOffset;
				float sqrDist = dot(v, v);
				float weight = max(0.0, exp(-sqrDist * 2.0) - 0.01111);
				weightSum += weight;
				float waveInput = dot(v, sideDir);
				heightAndSlope += vec3(cos(waveInput), -sin(waveInput) * sideDir) * weight;
			}
		}
		return heightAndSlope / weightSum;
	}

	// fBm-style erosion: each octave's gullies steer the next -> branching
	vec3 Erosion(in vec2 p, vec3 heightAndSlope, float scale, float strength,
		float slopePower, float cellScale, int octaves, float gain, float lacunarity) {
		vec3 inputHS = heightAndSlope;
		float freq = 1.0 / (scale * cellScale);
		strength *= scale;
		for (int i = 0; i < 8; i++) {
			if (i >= octaves) break;
			float sqrLen = dot(heightAndSlope.yz, heightAndSlope.yz);
			vec2 inputSlope = heightAndSlope.yz * pow(max(sqrLen, 1e-12), 0.5 * (slopePower - 1.0));
			heightAndSlope += Gullies(p * freq, inputSlope * cellScale) * strength * vec3(1, freq, freq);
			strength *= gain;
			freq *= lacunarity;
		}
		return heightAndSlope - inputHS;
	}
`;

// base for one stamp: a radial plug (flat top, smoothstep skirt — the shape of
// our massifs) + mild fBm irregularity, both with analytic derivatives
const STAMP_FRAG = /* glsl */`
	${EROSION_GLSL}
	uniform float uScale, uStrength, uPower, uCell;
	uniform int uOctaves;
	varying vec2 vUv;

	vec3 plugBase(vec2 p, vec2 seedOff) {
		float r = length(p);
		float t = clamp((r - 0.15) / 0.8, 0.0, 1.0);
		float h = 1.0 - (3.0*t*t - 2.0*t*t*t);
		float dhdr = -(6.0*t - 6.0*t*t) / 0.8;
		vec2 slope = r > 1e-5 ? dhdr * p / r : vec2(0.0);
		// mild fBm irregularity so plugs are not perfect cones
		vec3 n = vec3(0.0);
		float f = 3.0, a = 0.18;
		for (int i = 0; i < 3; i++) {
			n += noised(p * f + seedOff) * a * vec3(1.0, f, f);
			a *= 0.5; f *= 2.0;
		}
		return vec3(h * 0.55 + n.x, slope * 0.55 + n.yz);
	}

	void main() {
		// 2x2 atlas: each quadrant its own variant (hash offset)
		vec2 quad = floor(vUv * 2.0);
		vec2 p = fract(vUv * 2.0) * 2.0 - 1.0; // -1..1 within the quadrant
		vec2 seedOff = quad * 37.71 + 11.13;

		vec3 hs = plugBase(p, seedOff);
		vec3 ero = Erosion(p + seedOff, hs, uScale, uStrength, uPower, uCell,
			uOctaves, 0.5, 2.0);
		hs += ero;

		// tangent-space normal from the analytic slope
		vec3 nrm = normalize(vec3(-hs.y, -hs.z, 1.0));
		// erosion mask for crevice darkening (negative = carved)
		float mask = clamp(0.5 + ero.x / max(uScale * uStrength * 2.0, 1e-5), 0.0, 1.0);
		gl_FragColor = vec4(nrm.xy * 0.5 + 0.5, mask, clamp(hs.x, 0.0, 1.0));
	}
`;

let stampRT = null;
let bakedKey = '';

// bake (or re-bake when params changed) the 2x2 stamp atlas; needs a renderer
export function getStampTexture(renderer, params = {}) {
	const P = { ...STAMP_DEFAULTS, ...(globalThis.STAMP_PARAMS || null), ...params };
	const key = [P.scale, P.strength, P.power, P.cell, P.octaves].join('|');
	if (stampRT && bakedKey === key) return stampRT.texture;
	if (!renderer) return stampRT ? stampRT.texture : null;

	if (!stampRT) {
		stampRT = new WebGLRenderTarget(1024, 1024, {
			minFilter: LinearMipmapLinearFilter,
			magFilter: LinearFilter,
			generateMipmaps: true,
		});
		stampRT.texture.anisotropy = 4;
	}

	const scene = new Scene();
	const cam = new OrthographicCamera(-1, 1, 1, -1, 0, 1);
	const mat = new ShaderMaterial({
		vertexShader: 'varying vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }',
		fragmentShader: STAMP_FRAG,
		uniforms: {
			uScale: { value: P.scale },
			uStrength: { value: P.strength },
			uPower: { value: P.power },
			uCell: { value: P.cell },
			uOctaves: { value: P.octaves },
		},
	});
	scene.add(new Mesh(new PlaneGeometry(2, 2), mat));

	const prevRT = renderer.getRenderTarget();
	const prevXR = renderer.xr.enabled;
	renderer.xr.enabled = false;
	renderer.setRenderTarget(stampRT);
	renderer.render(scene, cam);
	renderer.setRenderTarget(prevRT);
	renderer.xr.enabled = prevXR;
	mat.dispose();

	bakedKey = key;
	console.log('mountain-stamps: baked 2x2 erosion stamp atlas', P);
	return stampRT.texture;
}
